/**
 * De lo que tipea el usuario al numero que viaja a la API.
 *
 * Vive en su propio archivo porque es la pieza mas peligrosa de todo el
 * frontend: es la que decide cuanta plata dice un campo. Un gasto de $183.450,35
 * tipeado como "183450.35" —que es lo que sale del teclado numerico y de copiar
 * y pegar de una factura o de un Excel— se guardaba como $18.345.035,00, cien
 * veces mas, sin un solo aviso. Ese importe se prorratea, se emite, se factura y
 * se manda por mail: los totales "cuadran" igual, porque cuadran contra el
 * numero equivocado.
 *
 * La causa era una regla de una linea: **todo punto es separador de miles**. En
 * es-AR eso es cierto la mayoria de las veces y falso justo cuando duele.
 *
 * La regla que lo reemplaza
 * -------------------------
 * No se adivina por locale: se mira la ESTRUCTURA de lo que se escribio, que es
 * lo que hace cualquier planilla seria.
 *
 * 1. Si estan los dos separadores, el que aparece **ultimo** es el decimal y el
 *    otro es de miles. `1.150.000,50` -> `1150000.50`; `1,150,000.50` ->
 *    `1150000.50`. Sirve igual para lo pegado de un Excel en ingles.
 * 2. Si solo hay comas: una sola coma es decimal (es lo que tipea cualquiera en
 *    es-AR); dos o mas son separadores de miles.
 * 3. Si solo hay puntos, son separadores de miles **unicamente** cuando la
 *    estructura es la de un agrupamiento de verdad: todos los grupos posteriores
 *    de exactamente tres digitos y el primero de uno a tres digitos sin cero
 *    adelante. `1.150.000` -> `1150000`, `183.450` -> `183450`. Cualquier otra
 *    cosa es un punto decimal: `183450.35`, `1234.5`, `0.5`, `12.3456`,
 *    `1.150.000.35`.
 *
 * Lo que queda ambiguo y por que se resuelve asi
 * ----------------------------------------------
 * `183.450` es genuinamente ambiguo: puede ser ciento ochenta y tres mil
 * cuatrocientos cincuenta, o ciento ochenta y tres con cuatrocientos cincuenta
 * milesimos. Gana el agrupamiento porque es la lectura argentina y porque tres
 * decimales no existen en un importe en pesos. La contrapartida —el usuario ve
 * el resultado ya formateado en cuanto sale del campo— es lo que hace que un
 * error de lectura sea visible y no silencioso.
 *
 * No conoce React ni la metadata a proposito: es una funcion pura sobre texto, y
 * es la unica del sistema que interpreta un numero escrito a mano.
 */

/** Un grupo de miles valido: exactamente tres digitos. */
const GRUPO = /^\d{3}$/;

/** El primer grupo de un numero agrupado: 1 a 3 digitos y sin cero adelante. */
const PRIMER_GRUPO = /^[1-9]\d{0,2}$/;

/**
 * `true` si esos puntos son separadores de miles y no un punto decimal.
 *
 * Se le pasa el numero SIN signo y ya sin comas (la regla 1 y 2 decidieron
 * antes). `partes` son los tramos entre puntos: `"1.150.000"` llega como
 * `["1", "150", "000"]`.
 */
function esAgrupamiento(partes: string[]): boolean {
  if (partes.length < 2) return false;
  if (!PRIMER_GRUPO.test(partes[0] ?? '')) return false;
  return partes.slice(1).every((parte) => GRUPO.test(parte));
}

/**
 * Lo que escribio el usuario, a la forma canonica que entiende `Number`.
 *
 * Devuelve texto y no `number` a proposito: el que llama tiene que poder
 * distinguir "todavia no es un numero" (`""`, `"-"`, `"1."`) de un valor, y
 * convertir a `number` aca perderia precision en los decimales que viajan como
 * string.
 */
export function parsearNumero(crudo: string): string {
  const limpio = (crudo ?? '').trim().replace(/\s/g, '');
  if (limpio === '') return '';

  const signo = limpio.startsWith('-') ? '-' : '';
  const cuerpo = limpio.replace(/^[+-]/, '');

  const ultimoPunto = cuerpo.lastIndexOf('.');
  const ultimaComa = cuerpo.lastIndexOf(',');

  // Regla 1: estan los dos. El ultimo manda.
  if (ultimoPunto >= 0 && ultimaComa >= 0) {
    const corte = Math.max(ultimoPunto, ultimaComa);
    const entera = cuerpo.slice(0, corte).replace(/[.,]/g, '');
    const decimal = cuerpo.slice(corte + 1);
    return `${signo}${entera}.${decimal}`;
  }

  // Regla 2: solo comas.
  if (ultimaComa >= 0) {
    const partes = cuerpo.split(',');
    if (partes.length > 2) return `${signo}${partes.join('')}`;
    return `${signo}${partes[0] ?? ''}.${partes[1] ?? ''}`;
  }

  // Regla 3: solo puntos.
  if (ultimoPunto >= 0) {
    const partes = cuerpo.split('.');
    if (esAgrupamiento(partes)) return `${signo}${partes.join('')}`;
    const entera = partes.slice(0, -1).join('');
    return `${signo}${entera}.${partes[partes.length - 1] ?? ''}`;
  }

  return `${signo}${cuerpo}`;
}
